import { gql, DocumentNode } from '@apollo/client';
import { Box, Typography } from '@mui/material';
import React from 'react';
import { Button } from '../../../Button';
import MuiModal, { MuiModalProps } from '../../../../mui/MuiModal/MuiModal';
import styles from './InvoiceViewPaymentInstallmentDetailsModal.module.scss';
import rpcShared from '@rockpapercoin/rpc-shared';
import {
	InstallmentStatus,
	InvoiceViewPaymentInstallmentDetailsModalInvoiceFragment,
	InvoiceViewPaymentInstallmentDetailsModalPaymentInstallmentFragment,
} from '#types';
import { InvoiceViewPaymentMethod } from '../InvoiceViewPaymentMethod';
import { InvoiceViewPaymentInstallmentStatus } from '../InvoiceViewPaymentInstallmentStatus';

const invoiceViewPaymentInstallmentDetailsModalPaymentInstallmentFragment = gql`
	fragment invoiceViewPaymentInstallmentDetailsModalPaymentInstallment on PaymentInstallment {
		id
		name
		amountDue
		status
		dueDate
		paymentDate
		gratuityPaid
		paymentNote
		amountRefunded
		refundDate
		refundNote
		...invoiceViewPaymentMethodPaymentInstallment
		...invoiceViewPaymentInstallmentStatus
	}
	${ InvoiceViewPaymentMethod.fragments
		.invoiceViewPaymentMethodPaymentInstallmentFragment }
	${ InvoiceViewPaymentInstallmentStatus.fragments
		.invoiceViewPaymentInstallmentStatusFragment }
`;

const invoiceViewPaymentInstallmentDetailsModalInvoiceFragment = gql`
	fragment invoiceViewPaymentInstallmentDetailsModalInvoice on Invoice {
		id
		discountAmount
		lineItems {
			id
			name
			taxPercentage
			rate
			quantity
		}
		paymentInstallments {
			...invoiceViewPaymentInstallmentDetailsModalPaymentInstallment
		}
		contact {
			...getViewingUserDocumentRelationshipContact
		}
		organization {
			country
			...getViewingUserDocumentRelationshipOrganization
		}
	}
	${ invoiceViewPaymentInstallmentDetailsModalPaymentInstallmentFragment }
	${ rpcShared.contacts.getViewingUserDocumentRelationshipContactFragment }
	${ rpcShared.organizations
		.getViewingUserDocumentRelationshipOrganizationFragment }
`;

const ModalValue: React.FC<{
	className?: string;
	label: string;
	value: string | React.JSX.Element;
	isBold?: true;
	isVisible?: boolean;
}> = ( { className, label, value, isBold, isVisible = true } ) =>
	isVisible ? (
		<Box className={ className }>
			<Typography color='gray' className={ styles.label }>
				{ label }
			</Typography>
			{ typeof value === 'string' ? (
				<Typography
					className={ isBold ? styles.bold : undefined }
					data-cy={ label.replace( ' ', '-' ).toLowerCase() }
				>
					{ value }
				</Typography>
			) : (
				value
			) }
		</Box>
	) : null;

export type InvoiceViewPaymentInstallmentDetailsModalProps = {
	paymentInstallment?: InvoiceViewPaymentInstallmentDetailsModalPaymentInstallmentFragment;
	invoice?: InvoiceViewPaymentInstallmentDetailsModalInvoiceFragment;
	requestingUserBaseId?: string;
	guestToken?: string;
} & Pick<MuiModalProps['muiModalProps'], 'onClose'>;

type InvoiceViewPaymentInstallmentDetailsModalPropsWithFragment =
	React.FC<InvoiceViewPaymentInstallmentDetailsModalProps> & {
		fragments: {
			invoiceViewPaymentInstallmentDetailsModalInvoiceFragment: DocumentNode;
			invoiceViewPaymentInstallmentDetailsModalPaymentInstallmentFragment: DocumentNode;
		};
	};

export const InvoiceViewPaymentInstallmentDetailsModal: InvoiceViewPaymentInstallmentDetailsModalPropsWithFragment =
	( {
		paymentInstallment,
		onClose,
		invoice,
		requestingUserBaseId,
		guestToken,
	} ) => {
		if ( !paymentInstallment ) {
			return null;
		}

		const currencyLabel = rpcShared.invoices.getCurrencyLabel(
			invoice?.organization?.country
		);

		const { viewingUserIsCustomer, viewingUserIsAPPA } =
			rpcShared.contacts.getViewingUserDocumentRelationship( {
				contact: invoice?.contact,
				viewingUserId: requestingUserBaseId,
			} );
		const { viewingUserIsMember: viewingUserIsVendorMember } =
			rpcShared.organizations.getViewingUserDocumentRelationship( {
				organization: invoice?.organization,
				viewingUserId: requestingUserBaseId,
			} );
		const viewingUserIsCustomerOrAPPA =
			viewingUserIsCustomer || viewingUserIsAPPA;

		const paymentInstallmentStringValues = invoice
			? rpcShared.invoices.calculateInstallmentToString(
				invoice,
				paymentInstallment
			  )
			: undefined;

		if ( !paymentInstallmentStringValues ) {
			return null;
		}

		return (
			<MuiModal
				muiModalProps={ {
					open: !!paymentInstallment,
					onClose,
					className: styles.container,
				} }
				title='Payment details'
				controls={
					<Button onClick={ ( e ) => onClose?.( e, 'fromCode' ) }>Done</Button>
				}
			>
				<Box className={ styles.columns }>
					<ModalValue
						label='Name'
						value={ paymentInstallmentStringValues.name }
						isBold
					/>
					<ModalValue
						label='Payment method'
						value={
							<InvoiceViewPaymentMethod
								paymentInstallment={ paymentInstallment }
								guestToken={ guestToken }
							/>
						}
					/>
					<ModalValue
						label={
							paymentInstallment.status === InstallmentStatus.Unpaid
								? `Amount due${ currencyLabel }`
								: `Amount paid${ currencyLabel }`
						}
						value={
							paymentInstallment.status === InstallmentStatus.Unpaid
								? paymentInstallmentStringValues.amountDue
								: paymentInstallmentStringValues.amountPaid
						}
						isBold
					/>
					<ModalValue
						label='Status'
						value={
							<InvoiceViewPaymentInstallmentStatus
								installment={ paymentInstallment }
								isCustomerOrAPPA={ viewingUserIsCustomerOrAPPA }
							/>
						}
					/>
				</Box>
				<Box className={ styles.row }>
					<ModalValue
						className={ styles.top }
						label='Due date'
						value={ paymentInstallmentStringValues.dueDate }
					/>
					<ModalValue
						className={ styles.bottom }
						label='Date paid'
						value={ paymentInstallmentStringValues.paymentDate }
					/>
					<ModalValue
						className={ styles.top }
						label='Tax'
						value={ paymentInstallmentStringValues.tax }
						isVisible={ paymentInstallmentStringValues.tax !== '$0.00' }
					/>
					<ModalValue
						className={ styles.bottom }
						label='Gratuity'
						value={ paymentInstallmentStringValues.gratuityPaid }
						isVisible={
							paymentInstallment.status !== InstallmentStatus.Unpaid &&
							paymentInstallmentStringValues.gratuityPaid !== '$0.00'
						}
					/>
				</Box>
				<ModalValue
					className={ styles.notes }
					label='Payment note'
					value={ paymentInstallment.paymentNote || '-' }
					isVisible={
						paymentInstallment.status !== InstallmentStatus.Unpaid &&
						viewingUserIsVendorMember
					}
				/>
				{ paymentInstallment.status &&
				[ InstallmentStatus.Refunded, InstallmentStatus.FullyPaidPartiallyRefunded, ].includes( paymentInstallment.status ) ? (
						<>
							<Box className={ styles.row }>
								<ModalValue
									className={ styles.top }
									label={ `Amount refunded${ currencyLabel }` }
									value={ paymentInstallmentStringValues.amountRefunded }
								/>
								<ModalValue
									className={ styles.top }
									label='Date refunded'
									value={ paymentInstallmentStringValues.refundDate }
								/>
							</Box>
							<ModalValue
								className={ styles.notes }
								label='Refund note'
								value={ paymentInstallment.refundNote || '-' }
								isVisible={ viewingUserIsVendorMember }
							/>
						</>
					) : null }
			</MuiModal>
		);
	};

InvoiceViewPaymentInstallmentDetailsModal.fragments = {
	invoiceViewPaymentInstallmentDetailsModalInvoiceFragment,
	invoiceViewPaymentInstallmentDetailsModalPaymentInstallmentFragment,
};
